import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { FormInput, FormSelect, FormTextarea } from '@/components/forms/FormField';
import {
  useQuotesList,
  useCreateQuote,
  useAcceptQuote,
  useConvertQuoteToInvoice,
  type Quote,
  type QuotesListParams,
  type QuoteStatus,
  type TaxRate,
  type CreateQuotePayload,
} from './hooks/useBilling';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useToast } from '@/components/ui/Toast';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_LINE_ITEMS = 100;

const TAX_RATE_OPTIONS = [
  { value: 'hst_13', label: 'HST (13%)' },
  { value: 'gst_5', label: 'GST (5%)' },
  { value: 'exempt', label: 'Exempt' },
];

const TAX_PERCENTAGES: Record<TaxRate, number> = {
  hst_13: 0.13,
  gst_5: 0.05,
  exempt: 0,
};

const STATUS_BADGE_MAP: Record<
  QuoteStatus,
  { variant: 'default' | 'info' | 'warning' | 'success' | 'danger' | 'outline'; label: string }
> = {
  draft: { variant: 'default', label: 'Draft' },
  sent: { variant: 'info', label: 'Sent' },
  accepted: { variant: 'success', label: 'Accepted' },
  expired: { variant: 'warning', label: 'Expired' },
  declined: { variant: 'danger', label: 'Declined' },
};

// ─── Validation Schema ────────────────────────────────────────────────────────

const lineItemSchema = z.object({
  description: z.string().min(1, 'Description is required').max(500),
  quantity: z.coerce.number().min(0.01, 'Quantity must be greater than 0').max(99999),
  unitPrice: z.coerce.number().min(0, 'Unit price must be 0 or greater').max(9999999.99),
  taxRate: z.enum(['hst_13', 'gst_5', 'exempt']),
});

const quoteSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  issueDate: z.string().min(1, 'Issue date is required'),
  expiryDate: z.string().min(1, 'Expiry date is required'),
  notes: z.string().max(2000).optional(),
  lineItems: z
    .array(lineItemSchema)
    .min(1, 'At least one line item is required')
    .max(MAX_LINE_ITEMS, `Maximum ${MAX_LINE_ITEMS} line items allowed`),
});

type QuoteFormValues = z.infer<typeof quoteSchema>;

/**
 * QuotesPage — quotes listing with create/edit form and convert-to-invoice action.
 * Requirements: 10.1
 */
export function QuotesPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { toast } = useToast();

  // ─── List State ─────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<QuoteStatus | ''>('');
  const [sort, setSort] = useState<SortState>({ column: 'createdAt', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const params: QuotesListParams = useMemo(
    () => ({
      page,
      pageSize,
      search: search || undefined,
      status: status || undefined,
      sortBy: sort.direction ? sort.column : undefined,
      sortDirection: sort.direction ?? undefined,
    }),
    [page, pageSize, search, status, sort],
  );

  const { data, isLoading } = useQuotesList(params);
  const acceptMutation = useAcceptQuote();
  const convertMutation = useConvertQuoteToInvoice();

  // ─── Table Columns ──────────────────────────────────────────────────────────
  const columns: ColumnDef<Quote>[] = useMemo(
    () => [
      {
        id: 'quoteNumber',
        header: 'Quote #',
        sortable: true,
        cell: (row) => (
          <span className="font-medium text-gray-900 dark:text-gray-100">{row.quoteNumber}</span>
        ),
      },
      {
        id: 'clientName',
        header: 'Client',
        accessorKey: 'clientName',
        sortable: true,
      },
      {
        id: 'total',
        header: 'Total',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span className="font-medium text-gray-900 dark:text-gray-100">
            ${row.total.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        cell: (row) => {
          const badge = STATUS_BADGE_MAP[row.status];
          return (
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
          );
        },
      },
      {
        id: 'expiryDate',
        header: 'Expires',
        sortable: true,
        cell: (row) => (
          <span className="text-gray-700 dark:text-gray-300">
            {new Date(row.expiryDate).toLocaleDateString('en-CA')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        align: 'right',
        cell: (row) => (
          <div className="flex items-center justify-end gap-2">
            {row.status === 'sent' && can(PERMISSIONS.BILLING_MANAGE_QUOTES) && (
              <Button variant="outline" size="sm" onClick={() => handleAccept(row.id)}>
                Accept
              </Button>
            )}
            {row.status === 'accepted' &&
              !row.convertedInvoiceId &&
              can(PERMISSIONS.BILLING_CREATE_INVOICE) && (
                <Button size="sm" onClick={() => handleConvert(row.id)}>
                  Convert to Invoice
                </Button>
              )}
            {row.convertedInvoiceId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/billing/invoices/${row.convertedInvoiceId}`)}
              >
                View Invoice
              </Button>
            )}
          </div>
        ),
      },
    ],
    [can, navigate],
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleAccept = async (quoteId: string) => {
    try {
      await acceptMutation.mutateAsync(quoteId);
      toast({ variant: 'success', title: 'Quote accepted' });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      toast({
        variant: 'error',
        title: error?.response?.data?.error?.message || 'Failed to accept quote',
      });
    }
  };

  const handleConvert = async (quoteId: string) => {
    try {
      await convertMutation.mutateAsync(quoteId);
      toast({ variant: 'success', title: 'Quote converted to invoice' });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      toast({
        variant: 'error',
        title: error?.response?.data?.error?.message || 'Failed to convert quote',
      });
    }
  };

  const pagination: PaginationState | undefined = data
    ? { page: data.page, pageSize: data.pageSize, total: data.total }
    : undefined;

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Quotes</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create and manage quotes for clients
          </p>
        </div>
        {can(PERMISSIONS.BILLING_MANAGE_QUOTES) && (
          <Button onClick={() => setShowCreateModal(true)}>Create Quote</Button>
        )}
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-48">
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as QuoteStatus | '');
                setPage(1);
              }}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'sent', label: 'Sent' },
                { value: 'accepted', label: 'Accepted' },
                { value: 'expired', label: 'Expired' },
                { value: 'declined', label: 'Declined' },
              ]}
              placeholder="All statuses"
            />
          </div>
          {status && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatus('');
                setPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* Data Table */}
      <DataTable<Quote>
        data={data?.quotes ?? []}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={isLoading}
        emptyMessage="No quotes found. Create your first quote to get started."
        filterValue={search}
        onFilterChange={handleSearchChange}
        filterPlaceholder="Search by quote number, client name..."
      />

      {/* Create Quote Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Quote"
        size="lg"
      >
        <QuoteCreateForm
          onSuccess={() => setShowCreateModal(false)}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>
    </div>
  );
}

// ─── Quote Create Form (Modal) ──────────────────────────────────────────────

interface QuoteCreateFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

function QuoteCreateForm({ onSuccess, onCancel }: QuoteCreateFormProps) {
  const { toast } = useToast();
  const createMutation = useCreateQuote();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      clientId: '',
      issueDate: new Date().toISOString().split('T')[0],
      expiryDate: '',
      notes: '',
      lineItems: [{ description: '', quantity: 1, unitPrice: 0, taxRate: 'hst_13' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const watchedLineItems = watch('lineItems');

  const totals = useMemo(() => {
    let subtotal = 0;
    const taxByRate: Record<string, { label: string; amount: number }> = {};

    (watchedLineItems ?? []).forEach((item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      const lineAmount = qty * price;
      subtotal += lineAmount;

      const taxRate = item.taxRate as TaxRate;
      const taxPct = TAX_PERCENTAGES[taxRate] ?? 0;
      const taxAmount = lineAmount * taxPct;

      if (taxPct > 0) {
        if (!taxByRate[taxRate]) {
          const label = TAX_RATE_OPTIONS.find((o) => o.value === taxRate)?.label ?? taxRate;
          taxByRate[taxRate] = { label, amount: 0 };
        }
        taxByRate[taxRate].amount += taxAmount;
      }
    });

    const taxTotal = Object.values(taxByRate).reduce((sum, t) => sum + t.amount, 0);
    const grandTotal = subtotal + taxTotal;

    return { subtotal, taxByRate, taxTotal, grandTotal };
  }, [watchedLineItems]);

  const onSubmit = async (values: QuoteFormValues) => {
    const payload: CreateQuotePayload = {
      clientId: values.clientId,
      issueDate: values.issueDate,
      expiryDate: values.expiryDate,
      notes: values.notes || undefined,
      lineItems: values.lineItems.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate as TaxRate,
      })),
    };

    try {
      await createMutation.mutateAsync(payload);
      toast({ variant: 'success', title: 'Quote created successfully' });
      onSuccess();
    } catch {
      toast({ variant: 'error', title: 'Failed to create quote' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      {/* Quote Details */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FormSelect
          name="clientId"
          control={control}
          label="Client"
          required
          options={[]}
          placeholder="Select a client"
        />
        <FormInput name="issueDate" control={control} label="Issue Date" type="date" required />
        <FormInput name="expiryDate" control={control} label="Expiry Date" type="date" required />
      </div>

      {/* Line Items */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Line Items</h3>
        {fields.map((field, index) => {
          const qty = Number(watchedLineItems?.[index]?.quantity) || 0;
          const price = Number(watchedLineItems?.[index]?.unitPrice) || 0;
          const lineAmount = qty * price;

          return (
            <div key={field.id} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4">
                <FormInput
                  name={`lineItems.${index}.description`}
                  control={control}
                  placeholder="Description"
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  name={`lineItems.${index}.quantity`}
                  control={control}
                  type="number"
                  min={0.01}
                  step="0.01"
                  placeholder="Qty"
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  name={`lineItems.${index}.unitPrice`}
                  control={control}
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Price"
                />
              </div>
              <div className="col-span-2">
                <FormSelect
                  name={`lineItems.${index}.taxRate`}
                  control={control}
                  options={TAX_RATE_OPTIONS}
                />
              </div>
              <div className="col-span-1 flex items-center justify-end">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  ${lineAmount.toFixed(2)}
                </span>
              </div>
              <div className="col-span-1 flex items-center justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(index)}
                  disabled={fields.length <= 1}
                  aria-label={`Remove line item ${index + 1}`}
                >
                  ✕
                </Button>
              </div>
            </div>
          );
        })}
        {fields.length < MAX_LINE_ITEMS && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({ description: '', quantity: 1, unitPrice: 0, taxRate: 'hst_13' })
            }
          >
            + Add Line Item
          </Button>
        )}
      </div>

      {/* Totals */}
      <div className="flex justify-end border-t border-gray-200 pt-3 dark:border-gray-700">
        <div className="w-56 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
            <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
          </div>
          {Object.entries(totals.taxByRate).map(([rate, { label, amount }]) => (
            <div key={rate} className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">{label}</span>
              <span>${amount.toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t pt-1 font-semibold dark:border-gray-700">
            <span>Total</span>
            <span>${totals.grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      <FormTextarea
        name="notes"
        control={control}
        label="Notes"
        placeholder="Additional notes..."
        rows={3}
      />

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting}>
          Create Quote
        </Button>
      </div>
    </form>
  );
}
